import { HttpClient, HttpContext, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  BYPASS_SPINNER,
  Cognito,
  EnvService,
  HttpOptions,
  LoggerService,
  OrgStore,
  Organization,
  User,
  UserOrg,
  Visitor
} from '@enliven/angular';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// Models
import { NewDlUser } from '../models/new-dl-user.model';
import { VisitorPayload } from '../models/visitor-payload.model';
import { VisitorApiResponse } from '../models/visitor-api-response.model';
import { VisitorSerializer } from '../classes/visitor-serializer';

// Services
import { E360SessionService } from './e360-session.service';
import { E360UserService } from './e360-user.service';

// Tech Debt - rework this service to separate concerns and use our ApiService methods
@Injectable({
  providedIn: 'root'
})
export class DataService {
  private readonly e360SessionService: E360SessionService = inject(E360SessionService);
  private readonly http: HttpClient = inject(HttpClient);
  private readonly e360UserService: E360UserService = inject(E360UserService);
  private readonly envService: EnvService = inject(EnvService);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly visitorSerializer: VisitorSerializer = inject(VisitorSerializer);

  private readonly bypassSpinnerContext: HttpContext = new HttpContext().set(BYPASS_SPINNER, true);

  readonly ORG_API = `${this.envService.orgApiUrl}/v2/api/organizations`;

  readonly USER_COGNITO_API = `${this.envService.userApiUrl}/v2/api/cognito`;
  readonly USER_ORG_API = `${this.envService.userApiUrl}/v2/api/user/organizations`;
  readonly VISITOR_API = `${this.envService.feAnalyticsApiUrl}/v1/api/analytics/organizations`;

  getOrgsPage(page: number, pageSize: number, search?: string): Observable<{ orgs: Organization[]; hasMore: boolean }> {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    if (search?.trim()) {
      params = params.set('search', search.trim());
    }

    return this.http
      .get<Organization[]>(this.ORG_API, {
        context: this.bypassSpinnerContext,
        params,
        observe: 'response'
      })
      .pipe(
        map((response: HttpResponse<Organization[]>) => {
          const orgs = response.body || [];
          const totalPageCount = this.getNumericHeader(response, ['total-page-count', 'Total-Page-Count']);
          const totalCount = this.getNumericHeader(response, ['total-count', 'Total-Count']);
          let hasMore = orgs.length === pageSize;
          if (totalPageCount > 0) {
            hasMore = page < totalPageCount;
          } else if (totalCount > 0) {
            hasMore = page * pageSize < totalCount;
          }
          return { orgs, hasMore };
        }),
        catchError(err => throwError(() => err))
      );
  }

  getOrgFromOrgApi(orgId: string): Observable<Organization> {
    return this.http
      .get<Organization>(`${this.ORG_API}/${orgId}`, {
        context: this.bypassSpinnerContext
      })
      .pipe(catchError(err => throwError(() => err)));
  }

  private getNumericHeader(response: HttpResponse<unknown>, headerNames: string[]): number {
    for (const headerName of headerNames) {
      const headerValue = response.headers.get(headerName);
      if (headerValue !== null) {
        const parsed = Number(headerValue);
        return Number.isNaN(parsed) ? 0 : parsed;
      }
    }

    return 0;
  }

  getAllStoresForOrg(orgId: string, params: HttpParams = undefined): Observable<OrgStore[]> {
    return this.http
      .get<OrgStore[]>(`${this.ORG_API}/${orgId}/stores`, {
        context: this.bypassSpinnerContext,
        params
      })
      .pipe(
        map((data: OrgStore[]) => data),
        catchError(err => throwError(() => err))
      );
  }

  // search is either idpExternalId from organization query param, or window.location.origin if idp external id is missing
  getCognitoInfoByOrg(search: string): Observable<Cognito> {
    return this.http.get<Cognito>(`${this.USER_COGNITO_API}/userpool/${search}`).pipe(
      map((data: Cognito) => data),
      catchError(err => throwError(() => err))
    );
  }

  getUserByOrg(organizationId: string, cognitoId: string): Observable<User> {
    return this.http.get<User>(`${this.USER_ORG_API}/${organizationId}/users/${cognitoId}`).pipe(
      map((data: User) => {
        this.e360UserService.user = data;
        return data;
      }),
      catchError(err => throwError(() => err))
    );
  }

  getOrgById(organizationId: string, idToken?: string): Observable<UserOrg> {
    const opts: HttpOptions = new HttpOptions();
    if (idToken) {
      const encodedIdToken: string = window.btoa(idToken);
      opts.params = opts.params.set('jwtIdToken', encodedIdToken);
    }
    return this.http.get<UserOrg>(`${this.USER_ORG_API}/${organizationId}`, opts).pipe(
      map((org: UserOrg) => {
        this.e360SessionService.organizationWebsiteBaseUrl = org.websiteBaseUrl;
        this.e360SessionService.organizationDefaultModuleId = org.defaultModuleId;
        this.e360SessionService.maxIdleTimeoutMinutes = org.maxIdleTimeout;
        this.e360SessionService.sessionTimeoutMinutes = org.sessionTimeout;
        return org;
      }),
      catchError(err => {
        const logMsg: string = `Error fetching org during ${idToken ? 'SSO' : 'direct'} log in attempt`;
        this.logger.error(logMsg, { organizationId, idToken }, err);
        return throwError(() => err);
      })
    );
  }

  getVisitor(organizationId: string, payload: VisitorPayload): Observable<Visitor> {
    const visitorPayloadForApi = this.visitorSerializer.toApiPayload(payload);

    return this.http
      .post<VisitorApiResponse>(`${this.VISITOR_API}/${organizationId}/visitor/resolve`, visitorPayloadForApi, {
        context: this.bypassSpinnerContext
      })
      .pipe(
        map((response: VisitorApiResponse) => this.visitorSerializer.fromApiResponse(response)),
        catchError(err => throwError(() => err))
      );
  }

  upsertSsoUser(organizationId: string): Observable<User> {
    const idToken = this.e360SessionService.idToken;
    const accessToken = this.e360SessionService.accessToken;
    const payload = { organizationId, accessToken, idToken };

    return this.http.post<User>(`${this.USER_ORG_API}/${organizationId}/users`, payload).pipe(
      map((user: User) => {
        this.e360UserService.user = user;
        return user;
      }),
      catchError(err => {
        this.logger.error('Error upserting SSO user during log in', { payload }, err);
        return throwError(() => err);
      })
    );
  }

  patchUser(payload: User): Observable<User> {
    return this.http.patch<User>(`${this.USER_ORG_API}/${payload.organizationId}/users/${payload.id}`, payload).pipe(
      map((user: User) => user),
      catchError(err => throwError(() => err))
    );
  }

  validateFirstTimeUser(orgIdpExternalId: string, payload: NewDlUser): Observable<User> {
    return this.http.post<User>(`${this.USER_ORG_API}/idpexternalid/${orgIdpExternalId}/cognito`, payload).pipe(
      map((data: User) => data),
      catchError(err => throwError(() => err))
    );
  }
}
